import { COUNTRY_CODES } from '../components/Common/constants';
import SriLankaFlag from '../assets/images/home/srilankaRound.svg';
import SingaporeFlag from '../assets/images/home/singaporeRound.svg';

/**
 * Country-agnostic URL prefix. Dashboard routes such as /all/dashboard/mybooking deliberately
 * sit outside any market, so this segment must never trigger a redirect.
 */
export const COUNTRY_AGNOSTIC_SEGMENT = 'all';

/** Flags bundled with the app, used when the registry has no uploaded flag for a market. */
const BUNDLED_FLAGS = {
  LK: SriLankaFlag,
  SG: SingaporeFlag,
};

/**
 * Markets to fall back on when the country registry endpoint is unavailable. Only the markets
 * that are actually live are listed; COUNTRY_CODES also carries entries used purely for phone
 * number prefixes.
 */
export const FALLBACK_COUNTRIES = [
  COUNTRY_CODES.SRI_LANKA,
  COUNTRY_CODES.SINGAPORE,
].map((entry, index) => ({
  code: entry.country_shortCode,
  name: entry.country_longCode,
  urlPath: entry.country_path,
  currency: entry.currency,
  dialCode: entry.code,
  flagUrl: BUNDLED_FLAGS[entry.country_shortCode] || null,
  lat: entry.lat,
  lng: entry.lng,
  defaultCountry: entry.country_shortCode === 'LK',
  order: index + 1,
}));

/** Shapes a registry row from the backend, filling in anything it does not carry. */
export const normaliseCountry = (raw) => ({
  code: raw.code,
  name: raw.name,
  urlPath: raw.urlPath || (raw.name || '').replace(/\s+/g, '-'),
  currency: raw.currency,
  dialCode: raw.dialCode,
  flagUrl: raw.flagUrl || BUNDLED_FLAGS[raw.code] || null,
  lat: raw.lat,
  lng: raw.lng,
  defaultCountry: !!raw.defaultCountry,
  order: raw.order || 0,
});

export const getDefaultCountry = (countries = []) =>
  countries.find((country) => country.defaultCountry) || countries[0] || null;

export const findCountryByCode = (countries = [], code) => {
  if (!code) return null;
  const wanted = String(code).toUpperCase();
  return countries.find((country) => country.code?.toUpperCase() === wanted) || null;
};

/** Browser IANA time zones and the market a visitor there lands on. "Singapore" is a legacy alias. */
const TIME_ZONE_LANDING_CODES = {
  'Asia/Singapore': 'SG',
  Singapore: 'SG',
  'Asia/Colombo': 'LK',
};

/** Visitors in any time zone not listed above land on Sri Lanka. */
const LANDING_FALLBACK_CODE = 'LK';

export const getBrowserTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch (error) {
    return '';
  }
};

/**
 * Market for a visitor whose URL does not name one yet, chosen from the browser time zone.
 * A time zone whose market is inactive (missing from `countries`) lands on Sri Lanka instead.
 */
export const getLandingCountry = (countries = [], timeZone = getBrowserTimeZone()) => {
  const pool = countries && countries.length ? countries : FALLBACK_COUNTRIES;
  const code = TIME_ZONE_LANDING_CODES[timeZone] || LANDING_FALLBACK_CODE;
  return findCountryByCode(pool, code)
    || findCountryByCode(pool, LANDING_FALLBACK_CODE)
    || getDefaultCountry(pool);
};

export const findCountryByUrlPath = (countries = [], urlPath) => {
  if (!urlPath) return null;
  const wanted = decodeURIComponent(urlPath).toLowerCase();
  return countries.find((country) => {
    const path = country.urlPath?.toLowerCase();
    const nameAsPath = (country.name || '').replace(/\s+/g, '-').toLowerCase();
    const name = (country.name || '').toLowerCase();
    const code = country.code?.toLowerCase();
    return path === wanted || nameAsPath === wanted || name === wanted || code === wanted;
  }) || null;
};

/**
 * The API lists only active markets, so it decides which markets exist. Bundled entries only
 * fill in fields the API left out, such as a flag; a market the admin deactivated is dropped.
 */
export const mergeCountryRegistry = (apiCountries = []) => {
  const fallbackByCode = {};
  FALLBACK_COUNTRIES.forEach((country) => {
    if (country?.code) {
      fallbackByCode[String(country.code).toUpperCase()] = country;
    }
  });

  const byCode = {};
  (apiCountries || []).forEach((country) => {
    if (!country?.code) return;
    const key = String(country.code).toUpperCase();
    const fallback = fallbackByCode[key] || {};
    const filled = { ...fallback };
    Object.keys(country).forEach((field) => {
      if (country[field] !== null && country[field] !== undefined && country[field] !== '') {
        filled[field] = country[field];
      }
    });
    byCode[key] = filled;
  });

  return Object.values(byCode).sort((a, b) => (a.order || 0) - (b.order || 0));
};

/** First path segment, for example "Sri-Lanka" out of "/Sri-Lanka/space/123". */
export const getFirstPathSegment = (pathname = '') => {
  const path = typeof pathname === 'string' ? pathname : '';
  return path.split('?')[0].split('/').filter(Boolean)[0] || '';
};

/**
 * Works out which market a URL belongs to.
 *
 * @returns {{ country: object|null, segment: string, isCountryAgnostic: boolean, isKnown: boolean }}
 */
export const resolveCountryFromPath = (pathname = '', countries = []) => {
  const segment = getFirstPathSegment(pathname);
  const isCountryAgnostic = segment.toLowerCase() === COUNTRY_AGNOSTIC_SEGMENT;
  const pool = (countries && countries.length) ? countries : FALLBACK_COUNTRIES;
  const country = isCountryAgnostic ? null : findCountryByUrlPath(pool, segment);

  return {
    country,
    segment,
    isCountryAgnostic,
    isKnown: isCountryAgnostic || !!country,
  };
};

/** Same path, pointed at a different market. */
export const swapCountryInPath = (pathname = '', country) => {
  const urlPath = country?.urlPath || (country?.name || '').replace(/\s+/g, '-');
  if (!urlPath) return pathname;
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 0) return `/${urlPath}`;
  segments[0] = urlPath;
  return `/${segments.join('/')}`;
};

/**
 * Removes the city segment from results URLs (`/{country}/{city}/spaces` → `/{country}/spaces`).
 * Cities are market-specific and must not carry over when the user switches country.
 */
export const stripCityFromSpacesPath = (pathname = '') => {
  const segments = pathname.split('/').filter(Boolean);
  const spacesIndex = segments.findIndex((segment) => segment.toLowerCase() === 'spaces');
  if (spacesIndex < 2) {
    return pathname;
  }
  return `/${[segments[0], ...segments.slice(spacesIndex)].join('/')}`;
};

/** Query keys that pin a search to a place; dropped on market switch. */
export const LOCATION_SEARCH_QUERY_KEYS = ['locationOrSpace', 'lat', 'lng', 'location', 'address'];

export const omitLocationFromSearchQuery = (query = {}) => {
  const next = { ...query };
  LOCATION_SEARCH_QUERY_KEYS.forEach((key) => {
    delete next[key];
  });
  return next;
};

export const currencyForCountry = (countries = [], code) =>
  findCountryByCode(countries, code)?.currency || getDefaultCountry(countries)?.currency || 'LKR';

/** URL segment for a market, for building links. Falls back to the default market. */
export const countryPathForCode = (countries = [], code) =>
  findCountryByCode(countries, code)?.urlPath
  || getDefaultCountry(countries)?.urlPath
  || getDefaultCountry(FALLBACK_COUNTRIES)?.urlPath;
